import { useCallback, useMemo } from "react"
import { useMutation, useQuery } from "@apollo/client"
import { Badge, Box, HStack, Spinner, Stack, Table, Text, VStack, Checkbox } from "@chakra-ui/react"
import { toaster } from "@hydrologiq/shared/chakra/Toaster"
import {
  GetAllFeaturesDocument,
  GetTenantFeaturesDocument,
  SetTenantFeatureDocument,
} from "@hydrologiq/gql-experience/graphql"

interface OrganizationTenant {
  id: string
  name: string
  enabled: boolean
}

interface Organization {
  id: string
  name: string
  slug: string
  enabled: boolean
  tenants: OrganizationTenant[]
  seatLimit?: number | null
  memberCount: number
  createdAt: string
}

interface ManageSectionsDialogProps {
  organization: Organization
  onClose: () => void
  onUpdate: () => void
}

export const ManageSectionsDialog = ({ organization, onClose, onUpdate }: ManageSectionsDialogProps) => {
  // Determine primary tenant: first enabled tenant, or first tenant if none enabled
  const primaryTenant = useMemo(() => {
    return organization.tenants.find((t) => t.enabled) || organization.tenants[0]
  }, [organization.tenants])

  const { data: allFeaturesData, loading: featuresLoading } = useQuery(GetAllFeaturesDocument)
  const {
    data: tenantFeaturesData,
    loading: tenantFeaturesLoading,
    refetch: refetchTenantFeatures,
  } = useQuery(GetTenantFeaturesDocument, {
    variables: { tenantId: primaryTenant?.id || "" },
    skip: !primaryTenant?.id,
  })

  const [setTenantFeature, { loading: updating }] = useMutation(SetTenantFeatureDocument, {
    onCompleted: () => {
      toaster.success({ title: "Section updated successfully" })
      refetchTenantFeatures()
      onUpdate()
    },
    onError: (error) => {
      toaster.error({ title: "Failed to update section", description: error.message })
    },
  })

  const handleToggleFeature = useCallback(
    (featureId: string, currentEnabled: boolean) => {
      if (!primaryTenant?.id) {
        toaster.error({ title: "No tenant available" })
        return
      }

      setTenantFeature({
        variables: {
          input: {
            tenantId: primaryTenant.id,
            featureId,
            enabled: !currentEnabled,
            preview: false,
          },
        },
      })
    },
    [primaryTenant?.id, setTenantFeature]
  )

  const allFeatures = allFeaturesData?.getAllFeatures || []
  const tenantFeatures = tenantFeaturesData?.getTenantFeatures || []

  // Create a map of featureId -> enabled state for quick lookup
  const featureStateMap = useMemo(() => {
    const map = new Map<string, boolean>()
    tenantFeatures.forEach((tf: any) => {
      map.set(tf.featureId, tf.enabled)
    })
    return map
  }, [tenantFeatures])

  // Merge all features with their current state
  const featuresWithState = useMemo(() => {
    return allFeatures.map((feature: any) => ({
      id: feature.id,
      name: feature.name,
      enabled: featureStateMap.get(feature.id) || false,
    }))
  }, [allFeatures, featureStateMap])

  if (!primaryTenant) {
    return (
      <Stack gap={4}>
        <Text>This organization has no tenants associated with it.</Text>
        <Text fontSize="sm" color="gray.600">
          Please add a tenant to the organization before managing sections.
        </Text>
      </Stack>
    )
  }

  const isLoading = featuresLoading || tenantFeaturesLoading

  return (
    <Stack gap={6} align="stretch" width="100%">
      {/* Primary Tenant Info */}
      <Box>
        <Text fontSize="sm" fontWeight="bold" mb={2}>
          Managing sections for:
        </Text>
        <HStack gap={2}>
          <Text fontSize="md" fontWeight="medium">
            {primaryTenant.name}
          </Text>
          {primaryTenant.enabled ? (
            <Badge colorPalette="green" size="sm">
              Enabled
            </Badge>
          ) : (
            <Badge colorPalette="gray" size="sm">
              Disabled
            </Badge>
          )}
        </HStack>
      </Box>

      {/* Features List */}
      <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Sections
        </Text>
        <Text fontSize="sm" mb={4}>
          Enable or disable sections for this tenant
        </Text>

        {isLoading ? (
          <Spinner size="sm" />
        ) : featuresWithState.length === 0 ? (
          <Text color="gray.600" fontSize="sm">
            No sections available
          </Text>
        ) : (
          <Table.Root size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Section</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {featuresWithState.map((feature) => (
                <Table.Row key={feature.id}>
                  <Table.Cell>
                    <Text fontWeight="medium">{feature.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Checkbox.Root
                      checked={feature.enabled}
                      onCheckedChange={() => handleToggleFeature(feature.id, feature.enabled)}
                      disabled={updating}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Checkbox.Label ml={2}>{feature.enabled ? "Enabled" : "Disabled"}</Checkbox.Label>
                    </Checkbox.Root>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>
    </Stack>
  )
}
